/**
 * Opt-in HTTP request logging for comparing Android Chrome vs desktop (same code path).
 *
 * Enable on the device:  localStorage.setItem('shopeaseHttpTrace', '1')  then reload.
 * Disable:              localStorage.removeItem('shopeaseHttpTrace')
 *
 * Or at build time:       REACT_APP_HTTP_TRACE=1
 *
 * Logs: method, full URL, flattened request headers (Authorization redacted).
 * For multipart, the browser sets Content-Type + boundary — axios usually omits Content-Type
 * on FormData so the log may show no Content-Type on the outgoing config (expected).
 */

function envTraceOn() {
  return String(process.env.REACT_APP_HTTP_TRACE || '').trim() === '1';
}

export function isHttpTraceEnabled() {
  if (typeof window === 'undefined') return envTraceOn();
  try {
    if (window.localStorage?.getItem('shopeaseHttpTrace') === '1') return true;
  } catch {
    /* private / blocked storage */
  }
  return envTraceOn();
}

function redactAuthorization(value) {
  if (value == null || value === '') return value;
  const s = String(value);
  if (!/^Bearer\s+/i.test(s)) return '(non-bearer; hidden)';
  const tail = s.slice(-6);
  return `Bearer ***(${s.length} chars, tail …${tail})`;
}

/** Axios 1.x may use AxiosHeaders — normalize to a plain object for logging. */
export function axiosHeadersToPlain(headers) {
  if (!headers) return {};
  if (typeof headers.toJSON === 'function') {
    try {
      return headers.toJSON();
    } catch {
      /* fall through */
    }
  }
  const out = {};
  if (typeof headers.forEach === 'function') {
    headers.forEach((v, k) => {
      out[k] = v;
    });
    return out;
  }
  return { ...headers };
}

export function redactHeadersForLog(plain) {
  const o = { ...plain };
  const authKey = Object.keys(o).find((k) => k.toLowerCase() === 'authorization');
  if (authKey) o[authKey] = redactAuthorization(o[authKey]);
  return o;
}

/**
 * @param {import('axios').InternalAxiosRequestConfig} config
 * @param {string} [label] e.g. "axiosClient" | "adminAxios"
 */
export function logAxiosRequestTrace(config, label = 'axios') {
  if (!isHttpTraceEnabled() || !config) return;

  const method = (config.method || 'get').toUpperCase();
  const base = config.baseURL || '';
  const u = config.url || '';
  const fullUrl = u.startsWith('http') ? u : `${base}${u}`;
  const headers = redactHeadersForLog(axiosHeadersToPlain(config.headers));
  const isForm = typeof FormData !== 'undefined' && config.data instanceof FormData;
  let formSummary;
  if (isForm) {
    try {
      const keys = [];
      for (const k of config.data.keys()) {
        if (!keys.includes(k)) keys.push(k);
      }
      formSummary = { keys, note: 'multipart; boundary set by browser (not in config.headers)' };
    } catch {
      formSummary = { note: 'FormData (could not enumerate keys)' };
    }
  }

  console.info(`[HTTP_TRACE][${label}][req]`, {
    method,
    url: fullUrl,
    headers,
    timeout: config.timeout,
    withCredentials: config.withCredentials,
    dataType: isForm ? 'FormData' : config.data == null ? '(none)' : typeof config.data,
    formSummary,
  });
}
